import {
  closestCenter,
  pointerWithin,
  type Collision,
  type CollisionDetection,
} from '@dnd-kit/core';

type Droppable = Parameters<CollisionDetection>[0]['droppableContainers'][number];
type Point = { x: number; y: number };
type Rect = { left: number; top: number; right: number; bottom: number; width: number; height: number };

/** Distance from a point to the nearest edge of a rect; 0 when inside it. */
function distanceToRect(p: Point, r: Rect): number {
  const dx = Math.max(r.left - p.x, 0, p.x - r.right);
  const dy = Math.max(r.top - p.y, 0, p.y - r.bottom);
  return Math.hypot(dx, dy);
}

function distanceToCenter(p: Point, r: Rect): number {
  return Math.hypot(p.x - (r.left + r.width / 2), p.y - (r.top + r.height / 2));
}

/** Containers ranked by a pointer-based distance, as dnd-kit collisions. */
function rankByPointer(
  containers: Droppable[],
  rects: Map<Droppable['id'], Rect>,
  pointer: Point,
  distance: (p: Point, r: Rect) => number,
): Collision[] {
  return containers
    .flatMap((c) => {
      const r = rects.get(c.id);
      return r ? [{ id: c.id, data: { droppableContainer: c, value: distance(pointer, r) } }] : [];
    })
    .sort((a, b) => a.data.value - b.data.value);
}

/**
 * Pointer-only collision detection: the column is decided by the pointer, and
 * the slot inside it by the pointer too. The dragged card's own rect never
 * takes part.
 *
 * A 340px card grabbed near one edge has its center up to 170px away from the
 * cursor. Any rule that measures from that center follows the card, not the
 * hand: crossing the gap between two 40px collapsed rails flings the drop to
 * whichever rail the card's center happens to sit over, which is usually the
 * last one. So: the column under the pointer wins; with none under it, the
 * column whose box is nearest the pointer wins. Inside that column the target
 * is the one whose center is nearest the pointer. Without pointer coordinates
 * (keyboard) this falls back to `closestCenter` over everything.
 */
export const columnFirstCollision: CollisionDetection = (args) => {
  const pointer = args.pointerCoordinates;
  if (pointer == null) return closestCenter(args);

  const hit = pointerWithin(args)[0];
  const hitContainer =
    hit == null
      ? undefined
      : args.droppableContainers.find((c) => c.id === hit.id);
  let columnId = hitContainer?.data.current?.columnId;

  if (columnId == null) {
    const columns = args.droppableContainers.filter(
      (c) => c.data.current?.columnId != null,
    );
    const nearest = rankByPointer(columns, args.droppableRects, pointer, distanceToRect)[0];
    columnId = nearest?.data?.droppableContainer.data.current?.columnId;
  }
  if (columnId == null) return closestCenter(args);

  const inColumn = args.droppableContainers.filter(
    (c) => c.data.current?.columnId === columnId,
  );
  return rankByPointer(inColumn, args.droppableRects, pointer, distanceToCenter);
};
